// ---------------------------------------------------------------------------
// ExampleWebsite.js  —  /example-website
// ---------------------------------------------------------------------------
// Dedicated demo page that consolidates the "Example Website" sections
// (Board & Train, Memberships, Financing) that previously lived inside a
// collapsible on the home page. Consolidating them into a single scrollable
// page (with anchor deep-links) gives the marketing story a proper home,
// keeps the main landing page focused on the K9os product pitch, and
// gives us a dedicated URL to promote in SEO + link out to.
//
// Extras that ONLY appear on this page (per user request):
//   • RunningDog sprite (golden retriever galloping across the bottom).
//   • Paw-print custom cursor, enabled by adding `k9-paw-cursor` to <body>
//     while this page is mounted and stripped on unmount so the rest of
//     the site keeps the browser default cursor.
// ---------------------------------------------------------------------------
import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ChevronRight, Monitor, Smartphone, Contact, Store, Sparkles } from 'lucide-react';

import { PublicNav } from '../components/PublicNav';
import { RunningDog } from '../components/RunningDog';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { Reveal } from '../components/Reveal';
import { DemoLeadSection } from '../components/DemoLeadSection';
import {
  BoardTrainSection,
  MembershipsPublic,
  FinancingSection,
} from '../components/ExampleSections';

// ---------------------------------------------------------------------------
// Hero video sources — a chained list of MP4 URLs. The browser tries each
// <source> in order and plays the first that loads, so the player never
// shows a broken frame even if a CDN blips or blocks a hotlink.
//   1. PRIMARY  – Cloudinary demo (golden retriever, hotlink-friendly, CORS
//                 open, ~9 MB HD MP4). Rock-solid on every browser + region.
//   2. FALLBACK – Pexels dog-training clips (professional trainer working
//                 with a dog). Pexels license = free for commercial use,
//                 no attribution required, and these URLs work from real
//                 user browsers even though a bot / headless UA may 403.
// To swap in your own graduation video, drop an MP4 into
// /app/frontend/public/videos/ and set HERO_VIDEO_PRIMARY to
// '/videos/your-file.mp4'. The rest of the <video> markup stays the same.
// ---------------------------------------------------------------------------
const HERO_VIDEO_PRIMARY    = 'https://res.cloudinary.com/demo/video/upload/dog.mp4';
const HERO_VIDEO_FALLBACK_1 = 'https://videos.pexels.com/video-files/5967878/5967878-hd_1920_1080_30fps.mp4';
const HERO_VIDEO_FALLBACK_2 = 'https://videos.pexels.com/video-files/4008537/4008537-hd_1920_1080_25fps.mp4';

export default function ExampleWebsite() {
  const location = useLocation();

  // Enable paw-print cursor globally ONLY while this page is mounted.
  // Body class toggling is used (instead of inline styles) because the
  // cursor rules live in public/index.html and are scoped to
  // `body.k9-paw-cursor` + `html.dark body.k9-paw-cursor`.
  useEffect(() => {
    document.body.classList.add('k9-paw-cursor');
    return () => document.body.classList.remove('k9-paw-cursor');
  }, []);

  // SEO meta — dedicated title/description so this page ranks for
  // "K9os example website" style searches without competing with the
  // main product landing page.
  useEffect(() => {
    document.title = 'K9os Example Website — See a Full Trainer Site in Action';
    const setMeta = (name, content) => {
      let el =
        document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      "See a full example of a K9os-powered trainer website — Board & Train enrollment, monthly membership tiers, and financing options — all wired up like a live production site."
    );
    setMeta('og:title', 'K9os Example Website — Live Demo of a Trainer Site');
    setMeta(
      'og:description',
      'Board & Train enrollment, memberships, and financing on a real K9os-powered example site.'
    );
    setMeta('og:url', 'https://k9os.app/example-website');
  }, []);

  // Smooth-scroll to an anchor when the URL carries one (e.g. #memberships).
  // Deferred a tick so lazy-mounted sections have time to layout before we
  // jump to them.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    }
  }, [location.hash]);

  return (
    <div
      className='min-h-screen bg-[#FFF8F2] dark:bg-slate-950 flex flex-col'
      data-testid='page-example-website'
    >
      <PublicNav />
      <RunningDog />

      {/* Hero — explains what this page is and orients visitors to the
          in-page navigation so they can jump straight to the section
          they care about (Board & Train / Memberships / Financing).
          A dog video plays behind the whole hero as an ambient
          background — muted / autoplay / looping — with a dark gradient
          overlay so the H1 and CTAs stay crisp and readable. */}
      <section
        className='relative py-14 sm:py-20 text-white overflow-hidden isolate'
        data-testid='example-website-hero'
      >
        {/* Background video layer — sits behind all hero content via a
            lower z-index. `poster` is intentionally omitted so no still
            image flashes before the video starts; the parent section's
            dark bg-color is what shows for the ~200ms before playback
            begins. `pointer-events-none` keeps it non-interactive. */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload='auto'
          className='pointer-events-none absolute inset-0 -z-10 w-full h-full object-cover'
          aria-hidden='true'
          data-testid='example-website-video-player'
        >
          <source src={HERO_VIDEO_PRIMARY} type='video/mp4' />
          <source src={HERO_VIDEO_FALLBACK_1} type='video/mp4' />
          <source src={HERO_VIDEO_FALLBACK_2} type='video/mp4' />
        </video>
        {/* Dark gradient wash over the video so headline + text stay
            AA-contrast readable on any frame of the video. */}
        <div
          aria-hidden='true'
          className='pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-[#0B1220]/85 via-[#0F1A33]/80 to-[#1B3A87]/85'
        />
        {/* Existing brand blobs, softened so they read on top of the video */}
        <div
          aria-hidden='true'
          className='pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C8102E]/25 blur-3xl'
        />
        <div
          aria-hidden='true'
          className='pointer-events-none absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#93C5FD]/15 blur-3xl'
        />
        <div className='relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> Example Website &middot; Live Demo
          </div>
          <h1
            className='font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]'
            data-testid='example-website-h1'
          >
            A website, APP, CRM &amp; POS &mdash; all in one!
          </h1>
          <p className='mt-5 text-lg sm:text-xl text-slate-100 max-w-3xl mx-auto leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]'>
            This is a working example of what your K9os-powered trainer site can look like &mdash; Board &amp; Train enrollment, monthly membership tiers, and financing options wired up on the same platform your clients book, message, and pay through.
          </p>

          {/* Icon strip echoing the old home-page divider ("website · APP · CRM · POS") */}
          <div className='mt-8 flex items-center justify-center gap-3'>
            {[Monitor, Smartphone, Contact, Store].map((Icon, i) => (
              <div
                key={i}
                className='w-11 h-11 rounded-lg bg-white/10 border border-white/20 backdrop-blur flex items-center justify-center text-white'
                aria-hidden='true'
              >
                <Icon className='w-5 h-5' />
              </div>
            ))}
          </div>

          {/* In-page nav — anchor jumps to the three sections below */}
          <nav
            className='mt-8 flex flex-wrap justify-center gap-2.5'
            aria-label='Example website sections'
            data-testid='example-website-anchor-nav'
          >
            {[
              { href: '#board-train', label: 'Board & Train' },
              { href: '#memberships', label: 'Memberships' },
              { href: '#financing', label: 'Financing' },
            ].map((a) => (
              <a
                key={a.href}
                href={a.href}
                className='inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-sm font-semibold text-white backdrop-blur transition-colors'
                data-testid={`example-website-anchor-${a.href.slice(1)}`}
              >
                {a.label} <ChevronRight className='w-3.5 h-3.5' />
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Board & Train — first demo section */}
      <Reveal direction='left' delay={0}>
        <BoardTrainSection />
      </Reveal>

      {/* Memberships — 3-tier pricing grid from the backend */}
      <Reveal direction='right' delay={80}>
        <MembershipsPublic />
      </Reveal>

      {/* Financing */}
      <Reveal direction='left' delay={160}>
        <FinancingSection />
      </Reveal>

      {/* Ready to launch CTA — bridges into pricing/demo booking */}
      <section
        className='py-14 sm:py-20 bg-white dark:bg-[#0B1220] border-t border-slate-200 dark:border-slate-800'
        data-testid='example-website-cta'
      >
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
            <Sparkles className='w-3.5 h-3.5' /> Want a site like this for your business?
          </div>
          <h2 className='font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
            Every K9os site is built custom to your trainer brand.
          </h2>
          <p className='mt-4 text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed'>
            This example is a preview of what your K9os-powered site can do out of the box &mdash; Board &amp; Train intake, memberships, and financing options all wired to your own Stripe or PayPal account.
          </p>
          <div className='mt-8 flex flex-col sm:flex-row items-center justify-center gap-3'>
            <Link
              to='/pricing'
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='example-website-pricing-cta'
            >
              See pricing &amp; packages <ChevronRight className='w-4 h-4' />
            </Link>
            <Link
              to='/contact'
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 text-[#0B1220] dark:text-slate-100 hover:border-[#1B3A87] hover:bg-slate-50 dark:hover:bg-slate-700 font-bold text-sm transition-all'
              data-testid='example-website-demo-cta'
            >
              Book a free scoping call
            </Link>
          </div>
        </div>
      </section>

      {/* Lead form — same one used across regional landing pages */}
      <DemoLeadSection
        regionName='the World'
        regionSlug='example-website'
        eyebrow='See K9os in action on your business'
        headingOverride='Get a K9os site like this one built for your dog-training business'
      />

      <MessageGregBubble />
    </div>
  );
}
